import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import './radio.css';
import getRadioShowsList from '../../../api/graphql/queries/getRadioShowsList';
import getRadioTags from '../../../api/graphql/queries/getRadioTags';
import RadioShowsList from './RadioShowsList';
import RadioShowForm from './RadioShowForm';
import RadioTagsView from './RadioTagsView';
import RadioUpcomingView from './RadioUpcomingView';
import { multilangLabel } from './RadioFields';

/*
The Radio tab (PS manager only; see utils/cmsTabs.js), which replaces pspanel's radio
admin. Spec: radio-pspanel-to-ps-manager.md §6. Four sub-views:

  - Programs:          paged list, create/edit/delete;
  - Episodes:          paged list with a program filter, create/edit/delete;
  - Tags & categories: create (categories too), rename, delete;
  - Upcoming airings:  the next days in Madrid time, each linking to its show's form.

The container owns what more than one sub-view needs: the program list (the episode
filter and the episode form's program select), the tag list (the Tags view and the
form's selects), and the open form. A save or delete bumps refreshKey so the list on
screen refetches.

Everything is admin-only on the backend; an isAuth: false answer sends the editor to
/login, as the other tabs do.
*/

const VIEWS = [
  { key: 'programs', label: 'Programs' },
  { key: 'episodes', label: 'Episodes' },
  { key: 'tags', label: 'Tags & categories' },
  { key: 'upcoming', label: 'Upcoming airings' },
];

const RadioContainer = () => {
  const history = useHistory();
  const [view, setView] = useState('programs');
  const [programs, setPrograms] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [tagsError, setTagsError] = useState('');
  const [editor, setEditor] = useState(null); // { kind, id, parent }
  const [refreshKey, setRefreshKey] = useState(0);
  const [episodeParent, setEpisodeParent] = useState('');
  const [flash, setFlash] = useState('');

  const onAuthFail = useCallback(() => {
    console.log('Radio - User authentication failed!');
    history.push('/login');
  }, [history]);

  const loadTags = useCallback(async () => {
    setTagsError('');
    const result = await getRadioTags();
    setTagsLoading(false);
    if (!result) {
      setTagsError('Could not load the tags. Try again.');
      return;
    }
    if (!result.isAuth) {
      onAuthFail();
      return;
    }
    setTags(result.tags || []);
  }, [onAuthFail]);

  // Every program, for the selects. Any list call returns them; one row is the cheapest.
  const loadPrograms = useCallback(async () => {
    const result = await getRadioShowsList({ kind: 'programs', limit: 1, offset: 0 });
    if (result && result.isAuth && result.programs) setPrograms(result.programs);
  }, []);

  useEffect(() => {
    loadTags();
    loadPrograms();
  }, []);

  const refreshAfterChange = (message, { kind } = {}) => {
    setFlash(message);
    setRefreshKey(key => key + 1);
    if (kind === 'programs') loadPrograms();
    // usage counts change with every show save or delete
    loadTags();
  };

  const openEditor = (kind, id) => setEditor({ kind, id });
  const openCreate = (kind, parent) => setEditor({ kind, id: null, parent });

  const showEpisodesOf = (programId) => {
    setEpisodeParent(String(programId));
    setView('episodes');
  };

  const handleSaved = (show, { created, kind }) => {
    setEditor(null);
    const noun = kind === 'programs' ? 'program' : 'episode';
    const label = show ? `“${multilangLabel(show.title, show.slug)}” (#${show.id})` : '';
    refreshAfterChange(`${created ? 'Created' : 'Saved'} ${noun} ${label}.`, { kind });
  };

  const handleDeleted = (id, { kind }) => {
    setEditor(null);
    refreshAfterChange(`Deleted ${kind === 'programs' ? 'program' : 'episode'} #${id}.`, { kind });
  };

  return (
    <div className="radio-section w-full bg-white">
      <div className="radio-subtabs" role="tablist" aria-label="Radio">
        {VIEWS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={view === key}
            className={`radio-subtab${view === key ? ' on' : ''}`}
            onClick={() => {
              if (key === 'episodes' && view !== 'episodes') setEpisodeParent('');
              setView(key);
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {flash ? (
        <div className="radio-flash">
          <span>{flash}</span>
          <button type="button" onClick={() => setFlash('')}>Dismiss</button>
        </div>
      ) : null}

      {view === 'programs' || view === 'episodes' ? (
        <RadioShowsList
          key={view}
          kind={view}
          programs={programs}
          parentFilter={view === 'episodes' ? episodeParent : ''}
          refreshKey={refreshKey}
          onEdit={openEditor}
          onCreate={openCreate}
          onShowEpisodes={showEpisodesOf}
          onProgramsLoaded={setPrograms}
          onChanged={refreshAfterChange}
          onAuthFail={onAuthFail}
        />
      ) : null}
      {view === 'tags' ? (
        <RadioTagsView tags={tags} loading={tagsLoading} loadError={tagsError} onReload={loadTags} onAuthFail={onAuthFail} />
      ) : null}
      {view === 'upcoming' ? (
        <RadioUpcomingView refreshKey={refreshKey} onEdit={openEditor} onAuthFail={onAuthFail} />
      ) : null}

      {editor ? (
        <RadioShowForm
          key={`${editor.kind}-${editor.id}`}
          kind={editor.kind}
          id={editor.id}
          initialParent={editor.parent}
          programs={programs}
          tags={tags}
          onClose={() => setEditor(null)}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
          onAuthFail={onAuthFail}
        />
      ) : null}
    </div>
  );
};

export default RadioContainer;
